/**
 * Per-(company, user) local preferences (audit FF-06).
 *
 * FloorFile's stated deployment model is shared plant-floor tablets with fast
 * operator turnover (D28, FRONTLINE_UX_&_ADOPTION.md). Five preference keys were
 * global — `floorfile.checks.savedViews`, `ff.cars.view`, `floorfile.nav.pins`,
 * `floorfile.nav.collapsed`, `floorfile:equipment-view` — and none was cleared
 * on logout. Saved check views embed filter state (assignee ids, locations,
 * statuses, date ranges), so operator A's working set, and the modules A had
 * pinned, were presented to operator B after A signed out. Cross-COMPANY on any
 * device used for two tenants.
 *
 * Not a data breach — the underlying records stay permission- and
 * facility-filtered on fetch — but unintended disclosure of one user's working
 * set to the next, and inconsistent with the partitioning the rest of the app
 * already does: the offline outbox is a per-user Dexie partition, the TanStack
 * query cache is a per-user partition, and `lib/auth.tsx` scopes its own
 * offline-encryption flag by company two lines from where logout runs.
 *
 * Everything here degrades quietly. Private mode and quota failures make
 * preferences session-only, which is the correct failure for a preference.
 */

import { useMemo } from 'react'
import { useAuth } from './auth'

const NAMESPACE = 'ff.u'

export interface StorageScope {
  companyId: string
  userId: string
}

/**
 * The current scope, or undefined before sign-in. Every helper below accepts
 * `undefined` and no-ops, so a component can call this unconditionally at the
 * top and never branch — writing nothing while anonymous is the correct
 * behaviour for a per-user preference.
 */
export function useStorageScope(): StorageScope | undefined {
  const { user, companyId } = useAuth()
  return useMemo(
    () => (user && companyId ? { companyId, userId: user.id } : undefined),
    [user, companyId],
  )
}

/** `ff.u:<companyId>:<userId>:<name>` — the only key shape feature code writes. */
export function userKey(scope: StorageScope, name: string): string {
  return `${NAMESPACE}:${scope.companyId}:${scope.userId}:${name}`
}

export function readUserJson<T>(scope: StorageScope | undefined, name: string, fallback: T): T {
  if (!scope) return fallback
  try {
    const raw = localStorage.getItem(userKey(scope, name))
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

export function writeUserJson(scope: StorageScope | undefined, name: string, value: unknown): void {
  if (!scope) return
  try {
    localStorage.setItem(userKey(scope, name), JSON.stringify(value))
  } catch {
    // Private-mode/quota failures degrade to session-only state.
  }
}

export function readUserValue(scope: StorageScope | undefined, name: string): string | undefined {
  if (!scope) return undefined
  try {
    return localStorage.getItem(userKey(scope, name)) ?? undefined
  } catch {
    return undefined
  }
}

export function writeUserValue(scope: StorageScope | undefined, name: string, value: string): void {
  if (!scope) return
  try {
    localStorage.setItem(userKey(scope, name), value)
  } catch {
    // as above
  }
}

/**
 * Drop every preference this user left on this device. Called from logout
 * beside `clearIdentity()`, which is where the D24 §9 data-at-rest posture is
 * already applied to the persisted identity and query cache.
 *
 * Iterates a SNAPSHOT of the key list: `localStorage.key(i)` re-indexes as
 * entries are removed, so deleting while walking it live skips keys.
 */
export function clearUserScopedStorage(scope: StorageScope): void {
  const prefix = `${NAMESPACE}:${scope.companyId}:${scope.userId}:`
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(prefix)) localStorage.removeItem(key)
    }
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

export function removeUserValue(scope: StorageScope | undefined, name: string): void {
  if (!scope) return
  try {
    localStorage.removeItem(userKey(scope, name))
  } catch {
    // as above
  }
}
